import { useState } from 'react'
import { router, Link } from '@inertiajs/react'
import { AdminLayout } from '@/layouts/admin-layout'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'

interface Feedback {
  id: number
  category: string
  title: string
  status: string
  priority: string
  occurrences_count: number
  dev_instance_id: number | null
  created_at: string
}

interface Pagination {
  page: number
  count: number
  pages: number
  last: number
  from: number
  to: number
  prev: number | null
  next: number | null
}

interface Props {
  feedbacks: Feedback[]
  pagy: Pagination
  filters: {
    status: string
    category?: string
    priority?: string
    title_or_description_cont?: string
    s?: string
  }
  valid_categories: string[]
  valid_statuses: string[]
  valid_priorities: string[]
  stats: { new: number; acknowledged: number; planned: number; total: number }
}

const STATUS_COLORS: Record<string, string> = {
  new: 'default',
  acknowledged: 'secondary',
  planned: 'warning',
  resolved: 'success',
  wont_fix: 'destructive',
}

const PRIORITY_COLORS: Record<string, string> = {
  low: 'secondary',
  medium: 'default',
  high: 'destructive',
}

function Index({ feedbacks, pagy, filters, stats, valid_categories, valid_statuses, valid_priorities }: Props) {
  const [searchQuery, setSearchQuery] = useState(filters.title_or_description_cont || '')

  const basePath = '/admin/platform_feedbacks'

  const buildParams = (overrides: Record<string, string | null> = {}) => {
    const params: Record<string, string> = {}
    if (filters.status && filters.status !== 'all') params.status = filters.status
    if (filters.category) params.category = filters.category
    if (filters.priority) params.priority = filters.priority
    if (searchQuery) params['q[title_or_description_cont]'] = searchQuery

    const urlParams = new URLSearchParams(window.location.search)
    const currentSort = urlParams.get('q[s]')
    if (currentSort) params['q[s]'] = currentSort

    for (const [key, value] of Object.entries(overrides)) {
      if (value === null || value === '') {
        delete params[key]
      } else {
        params[key] = value
      }
    }
    return params
  }

  const handleFilter = (key: string, value: string) => {
    const params = buildParams({ [key]: value === 'all' ? null : value })
    router.get(basePath, params, { preserveState: true })
  }

  const handleSearch = () => {
    router.get(basePath, buildParams(), { preserveState: true })
  }

  const handleReset = () => {
    setSearchQuery('')
    router.get(basePath, {}, { preserveState: true })
  }

  const handleSort = (field: string) => {
    const urlParams = new URLSearchParams(window.location.search)
    const currentSort = urlParams.get('q[s]')
    let newSort = `${field} asc`
    if (currentSort === `${field} asc`) newSort = `${field} desc`
    router.get(basePath, { ...buildParams(), 'q[s]': newSort }, { preserveState: true })
  }

  const getSortIcon = (field: string) => {
    const urlParams = new URLSearchParams(window.location.search)
    const currentSort = urlParams.get('q[s]')
    if (currentSort === `${field} asc`) return '↑'
    if (currentSort === `${field} desc`) return '↓'
    return '⇅'
  }

  return (
    <div className="flex flex-1 flex-col">
      <div className="@container/main flex flex-1 flex-col gap-2">
        <div className="flex flex-col py-2">
          <div className="px-3">
            {/* Filter Bar */}
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="text-sm font-semibold whitespace-nowrap">
                Platform Feedback ({pagy.count})
              </span>
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Badge variant="default" className="text-[10px] px-1 py-0">{stats.new} new</Badge>
                <Badge variant="secondary" className="text-[10px] px-1 py-0">{stats.acknowledged} ack</Badge>
                <Badge variant="warning" className="text-[10px] px-1 py-0">{stats.planned} planned</Badge>
              </div>
              <div className="w-px h-4 bg-border" />
              <Select
                value={filters.status || 'new'}
                onValueChange={(v) => handleFilter('status', v)}
              >
                <SelectTrigger className="h-7 text-xs w-32">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {valid_statuses.map((s) => (
                    <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={filters.category || 'all'}
                onValueChange={(v) => handleFilter('category', v)}
              >
                <SelectTrigger className="h-7 text-xs w-36">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {valid_categories.map((c) => (
                    <SelectItem key={c} value={c}>{c.replace('_', ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={filters.priority || 'all'}
                onValueChange={(v) => handleFilter('priority', v)}
              >
                <SelectTrigger className="h-7 text-xs w-28">
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {valid_priorities.map((p) => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="w-px h-4 bg-border" />
              <Input
                type="text"
                placeholder="Search title or description..."
                className="h-7 text-xs w-48"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button onClick={handleSearch} className="h-7 px-2 text-xs">Search</Button>
              <Button onClick={handleReset} variant="ghost" className="h-7 px-2 text-xs">Reset</Button>
            </div>

            {/* Table */}
            <div className="bg-card rounded-lg border border-border overflow-hidden">
              <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 180px)', minHeight: '400px' }}>
                <table className="w-full">
                  <thead className="bg-muted sticky top-0 z-10">
                    <tr>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">Title</th>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">Category</th>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">Priority</th>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">Status</th>
                      <th
                        className="px-3 py-1.5 text-right text-xs font-medium text-muted-foreground cursor-pointer hover:bg-muted/80 select-none"
                        onClick={() => handleSort('occurrences_count')}
                      >
                        <div className="flex items-center justify-end gap-1">
                          Occurrences
                          <span className="text-gray-400">{getSortIcon('occurrences_count')}</span>
                        </div>
                      </th>
                      <th
                        className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground cursor-pointer hover:bg-muted/80 select-none"
                        onClick={() => handleSort('created_at')}
                      >
                        <div className="flex items-center gap-1">
                          Created
                          <span className="text-gray-400">{getSortIcon('created_at')}</span>
                        </div>
                      </th>
                      <th className="px-3 py-1.5 text-right text-xs font-medium text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-card divide-y divide-border">
                    {feedbacks.length > 0 ? (
                      feedbacks.map((fb) => (
                        <tr key={fb.id} className="hover:bg-muted transition-colors">
                          <td className="px-3 py-1.5">
                            <span className="text-sm font-medium text-foreground">{fb.title}</span>
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap">
                            <Badge variant="secondary" className="text-xs">{fb.category.replace('_', ' ')}</Badge>
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap">
                            <Badge variant={PRIORITY_COLORS[fb.priority] as any} className="text-xs">{fb.priority}</Badge>
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap">
                            <Badge variant={STATUS_COLORS[fb.status] as any} className="text-xs">{fb.status.replace('_', ' ')}</Badge>
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap text-right">
                            <span className="text-sm text-foreground">{fb.occurrences_count}</span>
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap">
                            <span className="text-sm text-foreground">
                              {new Date(fb.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </span>
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap text-right">
                            <Link
                              href={`${basePath}/${fb.id}`}
                              className="text-sm text-blue-600 hover:text-blue-900"
                            >
                              View
                            </Link>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={7} className="px-3 py-8 text-center text-sm text-muted-foreground">
                          No feedback found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {pagy.pages > 1 && (
                <div className="px-3 py-1.5 flex items-center justify-between border-t border-border bg-card text-xs">
                  <span className="text-muted-foreground">
                    {pagy.from}-{pagy.to} of {pagy.count}
                  </span>
                  <div className="flex gap-1">
                    {pagy.prev && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs"
                        onClick={() => router.get(basePath, { ...buildParams(), page: pagy.prev }, { preserveState: true })}
                      >
                        Previous
                      </Button>
                    )}
                    {pagy.next && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs"
                        onClick={() => router.get(basePath, { ...buildParams(), page: pagy.next }, { preserveState: true })}
                      >
                        Next
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

Index.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default Index
